import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiGet, apiPost, ApiError } from './api';
import { PORTAL_ENABLED } from './feature-flags';
import type { SessionUser } from '@shared';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  refresh: async () => {},
  signOut: async () => {},
});

/**
 * Decide whether the current page needs to know about auth state.
 *
 * Marketing pages never display anything that depends on the signed-in
 * user, so we skip the /api/auth/me probe and avoid the 401 noise in
 * browser DevTools + API logs. The /portal and /admin route trees always
 * call refresh() (directly via RequireAuth or after sign-in), so direct
 * navigation to those URLs still works without the boot probe.
 */
function pageNeedsAuth(): boolean {
  if (PORTAL_ENABLED) return true;
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p.startsWith('/portal') || p.startsWith('/admin');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await apiGet<{ user: SessionUser }>('/api/auth/me');
      setUser(r.user);
    } catch (err) {
      // 401 = not signed in. Anything else = leave previous user (network blip etc).
      if (err instanceof ApiError && err.status === 401) setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout', {});
    } catch {
      /* ignore — server may already have invalidated */
    }
    setUser(null);
  }, []);

  useEffect(() => {
    if (pageNeedsAuth()) {
      refresh();
    } else {
      // Mark the provider ready without making a network call.
      setLoading(false);
    }
  }, [refresh]);

  return <Ctx.Provider value={{ user, loading, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}
